import type { Ssm5DiagramModule, Ssm5Dtc } from '@packages/domain/ssm5';

import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5DtcFilterKey, Ssm5DtcFilters } from '../helpers/dtc-filters';
import type { ModuleMessage } from '../helpers/module-messages';

import { DTC_FILTERS, filterDtcModules } from '../helpers/dtc-filters';
import { CLEAR_MESSAGE, FILTERED_OUT_MESSAGE, NOT_COMMUNICATING_MESSAGE } from '../helpers/module-messages';
import { ssm5FreezeFramesPath } from '../helpers/ssm5-paths';
import { Ssm5Card } from './ssm5-card';
import { Ssm5DtcFilterSwitch } from './ssm5-dtc-filter-switch';
import { Ssm5DtcRow } from './ssm5-dtc-row';
import { Ssm5ModuleMessage } from './ssm5-module-message';

/** Props for {@link Ssm5ModuleDtcsTab}. */
export type Ssm5ModuleDtcsTabProps = {
    /** Which filters of the DTC list are switched on. */
    filters: Ssm5DtcFilters;
    /** The module the panel shows. */
    module: Ssm5DiagramModule;
    /** Receives the filter and its checked state when a switch is pressed. */
    onFilterChange: (key: Ssm5DtcFilterKey, isChecked: boolean) => void;
    /** The VIN of the session, for the FFD address of each DTC. */
    vin: string;
};

/** A row of the tab: a DTC and the FFD address of the DTC in its system. */
type DtcRow = {
    /** The DTC. */
    dtc: Ssm5Dtc;
    /** The FFD detail address of the DTC in its VIN session. */
    freezeFramesPath: string;
};

const useStyles = createUseStyles({
    // The same inset as the Status and DTCs panel: the 1px left border of the panel comes off the left padding.
    content: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        padding: '0 16px 16px 15px',
    },
    filters: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        padding: '16px 16px 16px 15px',
    },
    switches: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        gap: 16,
    },
});

/**
 * The DTC Codes tab of a module: the historical and secondary filters over the module's DTCs, drawn as the
 * Status and DTCs list draws them, in one card without a header. A module that does not answer gets the
 * not-communicating message, a module that reports no DTC the clear message, both in place of the filters
 * and the card; when the filters hide every DTC, the tab says so as the list does.
 *
 * @param props - {@link Ssm5ModuleDtcsTabProps}
 * @returns the tab body
 */
export function Ssm5ModuleDtcsTab({ filters, module, onFilterChange, vin }: Ssm5ModuleDtcsTabProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const isCommunicating = module.status !== 'noConnection';
    const hasDtcs = isCommunicating && module.dtcModules.length > 0;
    const rows: DtcRow[] = filterDtcModules(module.dtcModules, filters).flatMap((dtcModule) =>
        dtcModule.dtcs.map((dtc) => ({
            dtc,
            freezeFramesPath: ssm5FreezeFramesPath(vin, { dtcCode: dtc.code, module: dtcModule.system }),
        }))
    );
    const message = tabMessage(isCommunicating, hasDtcs, rows.length);

    return (
        <>
            {hasDtcs && (
                <div className={classes.filters}>
                    <Typography variant="caption">{t('ssm5:dtcs.filters')}</Typography>
                    <div className={classes.switches}>
                        {DTC_FILTERS.map((filter) => (
                            <Ssm5DtcFilterSwitch
                                filter={filter}
                                isChecked={filters[filter.key]}
                                isEnabled
                                key={filter.key}
                                onChange={onFilterChange}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className={classes.content}>
                {message ? (
                    <Ssm5ModuleMessage message={message} />
                ) : (
                    <Ssm5Card>
                        {rows.map((row) => (
                            <Ssm5DtcRow
                                dtc={row.dtc}
                                freezeFramesPath={row.freezeFramesPath}
                                key={row.freezeFramesPath + JSON.stringify(row.dtc)}
                            />
                        ))}
                    </Ssm5Card>
                )}
            </div>
        </>
    );
}

/**
 * The message the tab shows instead of rows, if any: the module does not answer, it is clear, or the
 * filters hide every DTC.
 *
 * @param isCommunicating - whether the module answers
 * @param hasDtcs - whether the module reports a DTC
 * @param rowCount - how many DTCs the filters let through
 * @returns the message, or undefined when there are rows to show
 */
function tabMessage(isCommunicating: boolean, hasDtcs: boolean, rowCount: number): ModuleMessage | undefined {
    if (!isCommunicating) {
        return NOT_COMMUNICATING_MESSAGE;
    }

    if (!hasDtcs) {
        return CLEAR_MESSAGE;
    }

    return rowCount === 0 ? FILTERED_OUT_MESSAGE : undefined;
}
